import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setGuestUserDetails } from "./features/userSlice";
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';

const UserDetailsForm = ({
  values,
  setValues,
  showEmail = true,
  showMobile = true,
  buttonText,
  loading: parentLoading,
  className = "",
  isGuest = false,
}) => {
  const dispatch = useDispatch();
  const { userDetail } = useSelector((state) => state.userDetail);

  // Add form validation state
  const [errors, setErrors] = useState({});

  // Update form when userDetail changes (for logged-in users)
  useEffect(() => {
    if (!isGuest && userDetail) {
      setValues(prev => ({
        ...prev,
        firstName: userDetail.firstName || "",
        lastName: userDetail.lastName || "",
        mobile: userDetail.mobile ? userDetail.mobile.toString() : "",
        email: userDetail.email || "",
        countryCode: userDetail.countryCode || "+91",
        gender: userDetail.gender || "",
      }));
    }
  }, [userDetail, isGuest, setValues]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const finalValue = name === 'mobile' ? value : value.trim();

    // Clear error when user types
    setErrors(prev => ({
      ...prev,
      [name]: ''
    }));

    // Update local state first
    setValues(prev => {
      const newValues = {
        ...prev,
        [name]: finalValue
      };

      // Only update guest user details if in guest mode
      // Use the updated values instead of the old ones
      if (isGuest) {
        dispatch(setGuestUserDetails(newValues));
      }

      return newValues;
    });
  };

  // Add real-time validation
  const validateField = (name, value) => {
    switch (name) {
      case 'firstName':
        return !value.trim() ? 'First name is required' : '';
      case 'email':
        return !value.trim() ? 'Email is required' :
          !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) ? 'Invalid email format' : '';
      case 'mobile':
        return !value || value.length < 10 ? 'Valid mobile number is required' : '';
      default:
        return '';
    }
  };

  // Add onBlur handler for real-time validation
  const handleBlur = (e) => {
    const { name, value } = e.target;
    const error = validateField(name, value);
    setErrors(prev => ({
      ...prev,
      [name]: error
    }));

    // Only update guest user details if in guest mode and no errors
    if (isGuest && !error) {
      dispatch(setGuestUserDetails({
        ...values,
        [name]: value.trim()
      }));
    }
  };

  return (
    <form className={className}>
      <div className="row">
        <div className="col-md-6 mb-3">
          <label>First Name<span className="text-danger">*</span></label>
          <input
            type="text"
            className={`form-control ${errors.firstName ? 'is-invalid' : ''}`}
            name="firstName"
            value={values.firstName}
            onChange={handleChange}
            onBlur={handleBlur}
            maxLength={25}
            required
          />
          {errors.firstName && (
            <div className="invalid-feedback">{errors.firstName}</div>
          )}
        </div>
        <div className="col-md-6 mb-3">
          <label>Last Name</label>
          <input
            type="text"
            className="form-control"
            name="lastName"
            value={values.lastName}
            onChange={handleChange}
            onBlur={handleBlur}
            maxLength={25}
          />
        </div>
      </div>

      {showMobile && (
        <div className="mb-3">
          <label>Mobile Number<span className="text-danger">*</span></label>
          <div className="input-group">
            <div className="input-group-prepend">
              <PhoneInput
                country="in"
                value={`${values.countryCode}${values.mobile}`}
                onChange={(phone, data) => {
                  const dialCode = data.dialCode;
                  const phoneWithoutCode = phone.slice(dialCode.length);
                  handleChange({
                    target: {
                      name: 'mobile',
                      value: phoneWithoutCode
                    }
                  });
                  handleChange({
                    target: {
                      name: 'countryCode',
                      value: "+" + dialCode
                    }
                  });
                }}
                inputClass={`form-control ${errors.mobile ? 'is-invalid' : ''}`}
                containerClass="phone-input-container"
                specialLabel=""
                countryCodeEditable={false}
              />
            </div>
            {errors.mobile && (
              <div className="invalid-feedback d-block">{errors.mobile}</div>
            )}
          </div>
        </div>
      )}

      {showEmail && (
        <div className="mb-3">
          <label>Email<span className="text-danger">*</span></label>
          <input
            type="email"
            className={`form-control ${errors.email ? 'is-invalid' : ''}`}
            name="email"
            value={values.email}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="Enter email address"
            required
          />
          {errors.email && (
            <div className="invalid-feedback">{errors.email}</div>
          )}
        </div>
      )}
    </form>
  );
};

export default UserDetailsForm; 